/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Document Translator Credits RTK Query Service (S428/Changes 7, 8 — S429 URL fix)
 *
 * Reads the live word-credit balance from the DB ledger introduced in S428.
 *
 * Endpoint:
 *   GET /portal/dt-credits/balance/?machine_id=<machine_id>
 *   Response: { available_words: number; email: string }
 *
 * S429 URL-fix: Changed from /portal/dt-credits/ to /portal/dt-credits/balance/.
 * The original path conflicted with the portal app's dt-credits/ route (Stripe
 * invoice metadata). Both were mounted at api/v1/portal/ and Django resolved
 * the portal include first, making the DB-ledger view unreachable. The /balance/
 * suffix removes the conflict without touching the dashboard's useDtCredits hook.
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth, CACHE_TIMES, TAG_TYPES } from "@/redux/api-config";

// ── Types ───────────────────────────────────────────────────────────────────

export interface DtCreditsResponse {
  available_words: number;
  email: string;
}

// ── API ─────────────────────────────────────────────────────────────────────

export const dtCreditsApi = createApi({
  reducerPath: "dtCreditsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [TAG_TYPES.DT_CREDITS],
  // Credit balance changes on every translation — use SHORT cache (60 s).
  keepUnusedDataFor: CACHE_TIMES.SHORT,
  endpoints: (builder) => ({
    getDtCredits: builder.query<DtCreditsResponse, string>({
      // Arg is the machine_id string
      query: (machineId) =>
        `/portal/dt-credits/balance/?machine_id=${encodeURIComponent(machineId)}`,
      providesTags: (_result, _error, machineId) => [
        { type: TAG_TYPES.DT_CREDITS, id: machineId },
        { type: TAG_TYPES.DT_CREDITS, id: "BALANCE" },
      ],
      keepUnusedDataFor: CACHE_TIMES.SHORT,
    }),
  }),
});

export const { useGetDtCreditsQuery } = dtCreditsApi;
